import { useLazyQuery, useMutation } from '@apollo/client'
import IconHoverCopyByAMS from 'assets/images/icon-pacc-found.svg'
import classNames from 'classnames'
import { notify } from 'components/toastify'
import { styleJsonPacket } from 'constants/common'
import { MESSAGES, STATUS } from 'constants/messages'
import { GET_SENT_TEXT_HISTORY_QUERY, LOAD_LIST_TEMPLATE_SMS  } from 'queries/adminPortal'
import React, { memo, useEffect, useRef, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import Moment from 'react-moment'
import { tableHeaderTextHistoryTable } from './headers'

import { isAxiosError } from 'axios'
import Loading from 'components/loading'
import ButtonBase from 'components/partials/button'
import TotalNumberInTable from 'components/partials/totalNumberInTable'
import moment from 'moment'
import axiosInstance from 'network/axiosClient'
import DateFilterPicker from 'pages/developer-portal/table/activities-log/component/dateFilterPicker'
import SearchInput from 'pages/principal-account-management/components/searchInput'
import { IHistoryTextItem, ISentTextFilterItem } from '../../interface'
import ChatContentItemDialog, { ISMSTemplateFile } from 'pages/email-system-management/components/chat-screen'

interface Props {
  setIsRefetchList: (refetchUser: boolean) => void
  isRefetchList: boolean
}

const TextHistoryTable: React.FC<Props> = () => {
  const prevSearchObjectRef = useRef({})
  const [hasMoreTextData, setHasMoreTextData] = useState<boolean>(true)
  const [isOpenModalViewDetailText, setIsOpenModalViewDetailJob] = useState<boolean>(false)
  const [currentTextItemSelected, setCurrentTxtItemSelected] = useState<IHistoryTextItem>()
  const [listTextItemLog, setListTextItemLog] = useState<IHistoryTextItem[]>([])
  const [searchObject, setSearchObject] = useState<ISentTextFilterItem>({
    skip: 0,
    take: 20,
    searchValue: '',
  })
  const [minDateFilter, setMinDateFilter] = useState<Date>()
  const [maxDateFilter, setMaxDateFilter] = useState<Date>()
  const [isFocusSearchJobIdOrPhone, setIsFocusSearchJobIdOrPhone] = useState<boolean>(false)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [disableDownloadBtn, setDisableDownloadBtn] = useState<boolean>(false)
  const [listSMSTemplate , setListSMSTemplate] = useState<ISMSTemplateFile[]>([])

  const [
      loadListTemplateForSMS,
    ] = useLazyQuery(LOAD_LIST_TEMPLATE_SMS)
  
  const fetchMoreDataForTextHistory = async () => {
    try {
      const response = await getSentTextHistoryFetchMore({
        variables: {
          filter: {
            ...searchObject,
            skip: listTextItemLog.length,
            take: 20,
          },
        },
      })
      const { data, total } = response.data.getSentTextHistory
      setTotalCount(total)
      if (data.length > 0) {
        setHasMoreTextData(data.length === 20)
        const dataState = [...listTextItemLog, ...data]
        setTimeout(() => {
          setListTextItemLog(dataState)
        }, 1000)
      } else {
        setHasMoreTextData(false)
        return
      }
    } catch (error: any) {
      notify(error.message ?? MESSAGES.ERROR.E_TRY_AGAIN, STATUS.ERROR)
    }
  }

  const handleCloseInfoDialog = (reload?: boolean) => {
    setIsOpenModalViewDetailJob(false)
    if (reload) {
      handleGetData(searchObject)
    }
  }

  const onHandleClickViewDetailJobAction = (selectedPhone: string) => {
    if (!selectedPhone) {
      setCurrentTxtItemSelected(undefined)
    }
    setIsOpenModalViewDetailJob(true)
    if (listSMSTemplate.length === 0) {
      loadListTemplateForSMS({
        variables: {},
        fetchPolicy: 'no-cache',
      }).then((result) => {
        setListSMSTemplate(result.data.loadListTemplateForTxt)
      })
    }
  }

  const [
    getTextSentHistories,
    { loading: getSentTextHistoryLoading, fetchMore: getSentTextHistoryFetchMore },
  ] = useLazyQuery(GET_SENT_TEXT_HISTORY_QUERY)

  const handleGetData = async (searchObject: ISentTextFilterItem) => {
    try {
      const response = await getTextSentHistories({
        variables: {
          filter: searchObject,
        },
        fetchPolicy: 'no-cache',
      })
      if (response.data.getSentTextHistory) {
        const { data, total } = response.data.getSentTextHistory
        setTotalCount(total)
        setListTextItemLog(data)
      }
    } catch (error) {
      notify(MESSAGES.ERROR.E_TRY_AGAIN, STATUS.ERROR)
    }
  }

  const startDateChange = (value: Date) => {
    setMinDateFilter(value)
    const dateFrom = value ? moment(value).format('YYYY-MM-DD') : ''
    setSearchObject((state) => {
      return { ...state, dateFrom, take: 20, skip: 0 }
    })
  }
  const endDateChange = (value: Date) => {
    setMaxDateFilter(value)
    const dateTo = value ? moment(value).format('YYYY-MM-DD') : ''
    setSearchObject((state) => {
      return { ...state, dateTo, take: 20, skip: 0 }
    })
  }
  const handleSearchJobIdAndPhone = (value: string) => {
    setSearchObject((state) => {
      return { ...state, searchValue: value.trim(), take: 20, skip: 0 }
    })
  }

  useEffect(() => {
    if (
      searchObject &&
      JSON.stringify(searchObject) !== JSON.stringify(prevSearchObjectRef.current)
    ) {
      setHasMoreTextData(true)
      handleGetData(searchObject)
    }
    prevSearchObjectRef.current = searchObject
  }, [searchObject])

  const onClickDownloadCSVBtn = async () => {
    try {
      setDisableDownloadBtn(true)
      const response = await axiosInstance.post('/export/sent/text', searchObject, {
        responseType: 'blob',
      })

      const blob = new Blob([response.data])

      // Create a temporary URL for the Blob
      const url = URL.createObjectURL(blob)

      // Create a download link and initiate the file download
      const downloadLink = document.createElement('a')
      downloadLink.href = url
      downloadLink.download = 'sentText.csv'
      downloadLink.click()
    } catch (error: Error | any) {
      if (isAxiosError(error)) {
        notify(error.message, STATUS.ERROR)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    } finally {
      setDisableDownloadBtn(false)
    }
  }

  return (
    <div>
      <div className="flex justify-between mb-3">
        <p className="pt-2 text-headline5A text-neutral-1">Text History</p>
        <div className="flex items-end space-x-4">
          <TotalNumberInTable number={totalCount} />
          <ButtonBase
            className="disabled:bg-primary-shade3 mr-2"
            onClick={(e) => {
              e.stopPropagation()
              onHandleClickViewDetailJobAction('')
            }}
          >
            New Message
          </ButtonBase>
          <ButtonBase
            className="disabled:bg-primary-shade3 mr-2"
            bgHover="Download detail email job csv"
            onClick={onClickDownloadCSVBtn}
            disabled={disableDownloadBtn}
          >
            Download CSV
          </ButtonBase>
        </div>
      </div>
      <div className="flex w-full mb-5 grid grid-cols-3 gap-2">
        <DateFilterPicker
          setCallBackDateChange={startDateChange}
          maxDate={maxDateFilter}
          defaultDate={moment().subtract(1, 'month').toDate()}
        />
        <DateFilterPicker setCallBackDateChange={endDateChange} minDate={minDateFilter} />
        <div
          className={classNames('rounded-xl border-[2px]  w-full', {
            'border-neutral-3': isFocusSearchJobIdOrPhone,
            'border-neutral-5': !isFocusSearchJobIdOrPhone,
          })}
        >
          <SearchInput
            classInput="border-0 w-full"
            setIsFocus={setIsFocusSearchJobIdOrPhone}
            handleSearch={handleSearchJobIdAndPhone}
            placehoderText="Search box jobId or phone"
            isHandleSearch={false}
          />
        </div>
      </div>
      <table className="w-full text-left border-2 border-neutral-6 rounded-t-lg overflow-hidden">
        <thead className="bg-primary-shade4 text-body1 text-neutral-2 md:pr-4">
          <tr>
            {tableHeaderTextHistoryTable.map((item: any, idx) => (
              <th key={idx} className={`font-semibold py-5 pl-6 ${item.styles}`}>
                <div className={`flex`}>
                  <span className={`${item.isOneLineRes && 'lg:mt-[-3.5px]'}`}>{item.name}</span>
                  <div className={`${item.isOneLineRes && 'lg:mt-[-3.5px]'}`}></div>
                </div>
              </th>
            ))}
          </tr>
        </thead>
      </table>
      {getSentTextHistoryLoading && <Loading className="relative py-6" height={30} width={30} />}

      {listTextItemLog?.length === 0 && !getSentTextHistoryLoading ? (
        <p className="py-6 text-center text-body1 text-neutral-4">No data available.</p>
      ) : (
        Boolean(
          listTextItemLog !== null && listTextItemLog?.length > 0 && !getSentTextHistoryLoading,
        ) && (
          <InfiniteScroll
            dataLength={listTextItemLog?.length}
            next={fetchMoreDataForTextHistory}
            hasMore={hasMoreTextData}
            height={'max-content'}
            style={{ maxHeight: 500 }}
            loader={''}
            endMessage={''}
            className="cus-scrollbar"
          >
            {listTextItemLog?.map((textItem: IHistoryTextItem, index: number) => (
              <table key={index} className="cursor-pointer w-full">
                <tbody>
                  <tr className="h-[72px] w-full hover:bg-neutral-7 pt-5">
                    <td
                      className={` ${tableHeaderTextHistoryTable[0].rowWid} py-5 break-all pl-6 md:pl-1 align-top md:pl-6`}
                    >
                      <span>{textItem?.jobId}</span>
                    </td>
                    <td
                      className={`${tableHeaderTextHistoryTable[1].rowWid} py-5 break-all pl-6 md:pl-1 align-top md:pl-6`}
                    >
                      <span>{textItem?.to}</span>
                    </td>
                    <td
                      className={`${
                        tableHeaderTextHistoryTable[2].rowWid
                      } py-5 break-all pl-6 md:pl-3 align-top ${'md:pl-[20px]'}`}
                    >
                      <span>
                        {
                          <Moment format="yyyy-MM-DD HH:mm">
                            {moment.utc(textItem.sent_date ?? '')}
                          </Moment>
                        }
                      </span>
                    </td>
                    <td
                      className={`${tableHeaderTextHistoryTable[3].rowWid} py-5 break-all pl-6 md:pl-1 align-top md:pl-6`}
                    >
                      <span>{textItem?.source}</span>
                    </td>
                    <td
                      className={`${tableHeaderTextHistoryTable[4].rowWid} lg:pl-8 pl-1 md:${tableHeaderTextHistoryTable[3].rowWid} align-top`}
                    >
                      <div className="relative w-full break-all ">
                        <div className="h-[86px]">
                          <span
                            style={{ ...styleJsonPacket }}
                            className={`break-all lg:pl-2 pt-[19px] pl-6  pr-6`}
                          >
                            {textItem?.content}
                          </span>
                          <button
                            title="View Text"
                            onClick={(e) => {
                              e.stopPropagation()
                              onHandleClickViewDetailJobAction(textItem.to)
                              setCurrentTxtItemSelected(textItem)
                            }}
                          >
                            <img
                              src={IconHoverCopyByAMS}
                              alt="Icon-Hover-Copy"
                              className="absolute top-[24px] right-0"
                            />
                          </button>
                        </div>
                      </div>
                    </td>

                    <td
                      className={`${tableHeaderTextHistoryTable[5].rowWid} py-5 pl-8 break-all align-top`}
                    >
                      <span
                        className={`${
                          textItem.status === 'delivered' ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {' '}
                        {textItem?.status}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            ))}
          </InfiniteScroll>
        )
      )}
      {isOpenModalViewDetailText && (
        <ChatContentItemDialog
          key={(currentTextItemSelected?.jobId || '') + (currentTextItemSelected?.sent_date || '')}
          modalIsOpen={true}
          smsTemplates={listSMSTemplate}
          title="View Txt Detail"
          handleCloseDialog={handleCloseInfoDialog}
          userNumber={currentTextItemSelected?.to || ''}
        />
      )}
    </div>
  )
}
export default memo(TextHistoryTable)
